import axios from 'axios';
import type { RosterType } from '@interfaces/roster_type';
import type { StructureType } from '@interfaces/structure_type';
import type { DomainType } from '@interfaces/domain_type';
import type { FactionType } from '@interfaces/faction_type';
import type { HeroType } from '@interfaces/hero_type';
import { isRequestAborted } from '../utils/requestUtils';

const getLocalizedUrl = (path: string): string => {
  const locale = document.querySelector('meta[name="current-locale"]')?.getAttribute('content') || 'en';
  return `/${locale}${path}`;
};

/** Minimal payload for save: matches backend structure_params/roster_params. Avoids sending enriched data (available_units, image_paths, etc.) so the request is fast. */
function buildMinimalSavePayload(roster: RosterType, structure: StructureType): { roster: Record<string, unknown>; structure: Record<string, unknown> } {
  const rosterPayload = {
    user_id: roster.user_id,
    faction_id: roster.faction_id ?? undefined,
    domain_id: roster.domain_id ?? undefined,
    status: roster.status,
    name: roster.name ?? undefined,
    roster_format_id: roster.roster_format_id ?? undefined,
    point_cost: roster.point_cost ?? undefined,
  };

  const structurePayload = {
    max_points: structure.max_points,
    point_cost: structure.point_cost,
    overmaxed: structure.overmaxed,
    hero: structure.hero
      ? { id: structure.hero.id ?? undefined, tech_name: structure.hero.tech_name ?? undefined, point_cost: structure.hero.point_cost }
      : undefined,
    domain: structure.domain
      ? { id: structure.domain.id ?? undefined, tech_name: (structure.domain as { tech_name?: string }).tech_name ?? undefined }
      : undefined,
    faction: structure.faction
      ? { id: structure.faction.id ?? undefined, tech_name: (structure.faction as { tech_name?: string }).tech_name ?? undefined }
      : undefined,
    units: (structure.units ?? []).map((u) => ({
      id: u.id ?? undefined,
      tech_name: u.tech_name ?? undefined,
      quantity: u.quantity,
      point_cost: u.point_cost,
    })),
    spells: (structure.spells ?? []).map((s) => ({
      id: s.id ?? undefined,
      tech_name: s.tech_name ?? undefined,
      quantity: s.quantity,
    })),
    equipment: (structure.equipment ?? []).map((e) => ({
      id: e.id ?? undefined,
      tech_name: e.tech_name ?? undefined,
      quantity: e.quantity,
      point_cost: e.point_cost,
    })),
  };

  return { roster: rosterPayload, structure: structurePayload };
}

export const loadDomains = async (): Promise<DomainType[]> => {
  const url = getLocalizedUrl('/rosters/select_domain.json');
  try {
    const response = await axios.get(url);
    return response.data.domains.sort((a, b) => (a.weight ?? 1) - (b.weight ?? 1));
  } catch (error) {
    if (isRequestAborted(error)) return [];
    console.error("Failed to load domains:", error);
    return [];
  }
};

export const loadFactionsForDomain = async (
  domainId: number | null,
  domainTechName?: string | null
): Promise<FactionType[]> => {
  const url = getLocalizedUrl('/rosters/select_factions.json');
  try {
    const params: Record<string, string | number> = {};
    if (domainId != null) params.domain_id = domainId;
    else if (domainTechName) params.domain_tech_name = domainTechName;
    else return [];
    const response = await axios.get(url, { params });
    const factions = response.data.factions ?? [];
    return factions.sort((a: FactionType, b: FactionType) => (a.weight ?? 1) - (b.weight ?? 1));
  } catch (error) {
    if (isRequestAborted(error)) return [];
    console.error("Failed to load factions:", error);
    return [];
  }
};

export const loadHeroesForFaction = async (
  factionId: number | null,
  factionTechName?: string | null
): Promise<HeroType[]> => {
  const url = getLocalizedUrl('/rosters/select_heroes.json');
  try {
    const params: Record<string, string | number> = {};
    if (factionId != null) params.faction_id = factionId;
    else if (factionTechName) params.faction_tech_name = factionTechName;
    else return [];
    const response = await axios.get(url, { params });
    const heroes = response.data.heroes ?? [];
    return heroes.sort((a: HeroType, b: HeroType) => (a.weight ?? 1) - (b.weight ?? 1));
  } catch (error) {
    if (isRequestAborted(error)) return [];
    console.error("Failed to load heroes:", error);
    return [];
  }
};

export const loadHeroDetails = async (
  heroId: number | null,
  heroTechName?: string | null
): Promise<HeroType | null> => {
  const url = getLocalizedUrl('/rosters/hero_details.json');
  try {
    const params: Record<string, string | number> = {};
    if (heroId != null) params.hero_id = heroId;
    else if (heroTechName) params.hero_tech_name = heroTechName;
    else return null;
    const response = await axios.get(url, { params });
    return response.data;
  } catch (error) {
    if (isRequestAborted(error)) return null;
    console.error("Failed to load hero details:", error);
    return null;
  }
};

interface SaveResponse {
  url: string;
  id?: number;
}

export const saveNewStructure = async (roster: RosterType, structure: StructureType): Promise<SaveResponse | false> => {
  const url = getLocalizedUrl('/rosters.json');
  const { roster: rosterPayload, structure: structurePayload } = buildMinimalSavePayload(roster, structure);

  return await axios.post(url, { roster: rosterPayload, structure: structurePayload })
    .then(response => {
      const roster_id = response.data.result.id;
      localStorage.removeItem('roster');

      return { url: getLocalizedUrl('/rosters/' + roster_id.toString() + '/edit'), id: roster_id };
    })
    .catch(error => {
      if(error.response?.status === 401) {
        const parsed_roster = JSON.stringify(roster);
        localStorage.setItem('roster', parsed_roster);

        return { url: getLocalizedUrl('/users/sign_in') };
      } else {
        console.log(error);
        return false;
      }
  });
};

export const updateStructure = async (rosterId: number, roster: RosterType, structure: StructureType): Promise<void> => {
  const url = getLocalizedUrl(`/rosters/${rosterId}.json`);
  const { roster: rosterPayload, structure: structurePayload } = buildMinimalSavePayload(roster, structure);

  try {
    await axios.patch(url, { roster: rosterPayload, structure: structurePayload });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const loadRosters = async (): Promise<any> => {
  const url = getLocalizedUrl('/rosters.json');
  const response = await axios.get(url);
  return response.data;
};
